import { temperatureRank } from "./gulaConstants";

type OrderedLead = {
  created_at: string;
  lead_temperature?: string | null;
  stage?: string;
  recontact_at?: string | null;
  last_activity_at?: string | null;
};

/** Leads that came in during the last day stay on top, newest first. */
export const FRESH_LEAD_MS = 24 * 60 * 60 * 1000;

const isFresh = (lead: OrderedLead, now: number) =>
  now - new Date(lead.created_at).getTime() < FRESH_LEAD_MS;

/** A recontact for today or already late: the easiest call to close. */
const isRecontactDue = (lead: OrderedLead, now: number) => {
  if (lead.stage !== "recontact" || !lead.recontact_at) return false;
  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);
  return (
    new Date(`${lead.recontact_at.slice(0, 10)}T00:00:00`).getTime() <=
    endOfToday.getTime()
  );
};

const lastTouch = (lead: OrderedLead) =>
  new Date(lead.last_activity_at ?? lead.created_at).getTime();

/**
 * Board order, so the top card of each column is the next call:
 * recontacts due today (or late) first, then fresh leads (newest first), then
 * hot, warm, cold and unscored; within the same temperature, the lead nobody
 * has touched for longest comes first.
 */
export const compareLeads =
  (now: number) =>
  (a: OrderedLead, b: OrderedLead): number => {
    // «No contesta»: by the day they came in, newest first (4 nov)
    if (a.stage === "no_answer" && b.stage === "no_answer") {
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    }
    const dueA = isRecontactDue(a, now);
    const dueB = isRecontactDue(b, now);
    if (dueA !== dueB) return dueA ? -1 : 1;
    if (dueA && dueB) {
      return (a.recontact_at ?? "").localeCompare(b.recontact_at ?? "");
    }
    const freshA = isFresh(a, now);
    const freshB = isFresh(b, now);
    if (freshA && freshB) {
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    }
    if (freshA !== freshB) return freshA ? -1 : 1;
    return (
      temperatureRank(a.lead_temperature) -
        temperatureRank(b.lead_temperature) || lastTouch(a) - lastTouch(b)
    );
  };
